import {eventRoutes} from 'event/EventRoutes'
import {getEventImage, updateEventImagesAgregate} from 'event/models/Event'
import {useFormik} from 'formik'
import EventPage from 'organization/Event/EventPage'
import {EventFaqHelp} from 'organization/Event/Help/FAQ'
import {useOrganizationEvent} from 'organization/Event/OrganizationEventProvider'
import {SaveEventData} from 'organization/Event/Settings/SaveEvent'
import {Image, ImageSelector} from 'organization/ImageSelector'
import BreadcrumMenu from 'organization/theme/Breadcrum'
import React from 'react'
import {LocalLink} from 'i18n/LocalLink'
import {toast} from 'react-toastify'
import styled from 'styled-components'
import TextEditor from 'utils/ui/TextEditor'
import {useI18n} from 'i18n/I18nContext'
import {LOCALE_LABELS, SUPPORTED_LOCALES} from 'i18n/locales'

export const PublicPage = () => {
  const {t, locale} = useI18n()
  const {event, updateEvent, loading} = useOrganizationEvent()

  const {handleSubmit, handleBlur, handleChange, values, setValues} = useFormik(
    {
      enableReinitialize: true,
      initialValues: {
        short_description: event?.short_description,
        description: event?.description,
        video: event?.video,
        locale: (event as {locale?: string})?.locale ?? locale,
      },
      onSubmit: async (values: SaveEventData) => {
        const result = await updateEvent(values)

        if (result === true) {
          toast.success(t('settings.saved'))
        }
      },
    },
  )

  const handleDescriptionChange = (value: string) => {
    setValues({
      ...values,
      description: value,
    })
  }

  const changeEventImage = (type: number, updatedImage: Image | null) => {
    setValues({
      ...values,
      images: updateEventImagesAgregate(values.images, type, updatedImage),
    })
  }

  if (!event) return null

  const routes = eventRoutes(event)

  return (
    <EventPage>
      <>
        <div className="row pl-30 pb-30 pr-30">
          <BreadcrumMenu
            title={t('orgPublicPage.breadcrumb')}
            helpInfo={<EventFaqHelp faqKey="publicPage" />}
          />

          <div className="col-xxl-12">
            <div className="create__event-area">
              <div className="body__card-wrapper">
                <PreviewHolder className="pricing__item">
                  <PreviewText>
                    {event.is_public && (
                      <>
                        {' '}
                        <i className="flaticon-warning-1"></i>{' '}
                        {t('orgPublicPage.statusPublic')}
                      </>
                    )}
                    {!event.is_public && (
                      <>
                        {' '}
                        <i className="flaticon-warning-1"></i>{' '}
                        {t('orgPublicPage.statusPrivate')}
                        <br />
                        {t('orgPublicPage.privateHint')}
                      </>
                    )}
                  </PreviewText>
                  <LocalLink
                    to={{pathname: routes.root, search: '?preview=1'}}
                    target="_blank"
                    className="input__btn"
                    style={{width: 300}}
                  >
                    {t('orgPublicPage.preview')}
                  </LocalLink>
                </PreviewHolder>

                {/* create event form  */}
                <form onSubmit={handleSubmit}>
                  <div className="create__event-main pt-25">
                    <div className="event__left-box">
                      <div className="create__input-wrapper">
                        <div className="event__input mb-15">
                          <label className="input__field-text">
                            {t('orgPublicPage.shortDesc')}
                          </label>

                          <textarea
                            name="short_description"
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            {values.short_description}
                          </textarea>
                        </div>

                        <div className="event__input mb-15">
                          <label className="input__field-text">
                            {t('orgPublicPage.longDesc')}
                          </label>

                          <TextEditor
                            value={values.description ?? ''}
                            onChange={handleDescriptionChange}
                            fullModules
                          />
                        </div>

                        <div className="singel__input-field  mb-15">
                          <label className="input__field-text">
                            {t('orgPublicPage.youtube')}
                          </label>

                          <input
                            placeholder="https://www.youtube.com/watch?v=kC5949RveFc"
                            type="text"
                            name="video"
                            value={values.video}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </div>

                        <div>
                          <label className="input__field-text">
                            {t('orgPublicPage.language')}
                          </label>
                          <div className="contact__select">
                            <select
                              name="locale"
                              className="form-select"
                              value={String(
                                (values as SaveEventData).locale ?? locale,
                              )}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              {SUPPORTED_LOCALES.map((loc) => (
                                <option key={loc} value={loc}>
                                  {LOCALE_LABELS[loc]}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="event__right-box">
                      <div className="event__input mb-15">
                        <label className="input__field-text">
                          {' '}
                          {t('orgPublicPage.heroImage')} - <i>700 x 400</i>{' '}
                        </label>
                        <ImageSelector
                          onSelect={(image: Image | null) =>
                            changeEventImage(1, image)
                          }
                          preview={true}
                          selected={getEventImage(event, 1)}
                        />
                      </div>

                      <div className="event__input mb-15">
                        <label className="input__field-text">
                          {' '}
                          {t('orgPublicPage.videoImage')} - <i>600 x 350</i>{' '}
                        </label>
                        <ImageSelector
                          onSelect={(image: Image | null) =>
                            changeEventImage(2, image)
                          }
                          preview={true}
                          selected={getEventImage(event, 2)}
                        />
                      </div>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className={
                      loading
                        ? 'disableBtnStyle w-100 mt-30'
                        : 'input__btn w-100 mt-30'
                    }
                  >
                    {t('common.save')}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </>
    </EventPage>
  )
}

const PreviewHolder = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 15px 40px;
`

const PreviewText = styled.div`
  color: #333333;
  & > i {
    margin-inline-end: 10px;
    color: var(--clr-theme-1);
  }
`
